import type { ActiveRest } from '../lib/useRestTimer';

// RestTimerPill — fixed-position rest countdown shown above the bottom Nav.
// While running, it shows mm:ss + label + a "Skip rest" button that cancels
// both the local UI timer and the server-side push. On expiry it flips to the
// solid ink pill and shows "Done — start next set"; the user dismisses or it stays until
// they start the next set (which will replace the pill via start()).
export function RestTimerPill({
  active,
  secondsRemaining,
  onCancel,
  onDismiss,
}: {
  active: ActiveRest;
  secondsRemaining: number;
  onCancel: () => void;
  onDismiss: () => void;
}) {
  const expired = active.phase === 'expired';
  const mm = String(Math.floor(secondsRemaining / 60)).padStart(2, '0');
  const ss = String(secondsRemaining % 60).padStart(2, '0');
  const pct = clampPct(
    100 -
      (Math.max(0, active.expiresAtMs - Date.now()) / (active.totalSec * 1000)) *
        100,
  );
  return (
    <div
      className="fixed inset-x-0 z-30 flex justify-center pointer-events-none"
      style={{ bottom: 'calc(env(safe-area-inset-bottom, 0px) + 78px)' }}
      role="status"
      aria-live="polite"
    >
      {/* The same floating language as the nav capsule below it: a frosted white
          object with the one soft floating shadow. Expiry is the exception —
          it flips to the solid ink pill, the loudest signal the system has. */}
      <div
        className={`pointer-events-auto w-full max-w-md mx-3 overflow-hidden rounded-panel border backdrop-blur-2xl transition-colors shadow-[0_1px_2px_rgba(11,11,15,0.05),0_16px_40px_-16px_rgba(11,11,15,0.26)] ${
          expired
            ? 'bg-accent text-accent-fg border-accent animate-pulse'
            : 'bg-bg-raised/95 text-fg border-line'
        }`}
      >
        {!expired && (
          <div className="h-1 bg-bg-sunken">
            <div
              className="h-full bg-good-mid transition-all"
              style={{ width: `${pct}%` }}
            />
          </div>
        )}
        <div className="flex items-center gap-3 px-4 py-2.5">
          <span
            className={`font-display text-xl leading-none tabular-nums ${
              expired ? 'text-accent-fg' : 'text-fg'
            }`}
            aria-label={expired ? 'Rest done' : `${mm} minutes ${ss} seconds remaining`}
          >
            {expired ? 'DONE' : `${mm}:${ss}`}
          </span>
          <span className="flex-1 min-w-0 truncate text-sm">
            {expired ? 'Start next set — ' : 'Rest · '}
            <span className={expired ? 'text-accent-fg' : 'text-fg-muted'}>
              {active.label}
            </span>
          </span>
          {expired ? (
            <button
              type="button"
              onClick={onDismiss}
              className="font-mono text-[10px] uppercase tracking-[0.16em] text-accent-fg hover:underline"
            >
              dismiss
            </button>
          ) : (
            <button
              type="button"
              onClick={onCancel}
              className="font-mono text-[10px] uppercase tracking-[0.16em] text-fg-faint hover:text-fg transition-colors"
            >
              skip
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function clampPct(n: number): number {
  if (n < 0) return 0;
  if (n > 100) return 100;
  return n;
}
